import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, FileDown, CheckCircle2, XCircle, ClipboardList } from "lucide-react";
import api, { errMsg, inr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import OARecordModule from "../components/OARecordModule";
import OADocuments from "../components/OADocuments";
import WorkStatusBadge from "../components/WorkStatusBadge";

const TABS = [
  { key: "details", label: "OA Details" },
  { key: "office_work", label: "Office Work", rec: true },
  { key: "item", label: "Items", rec: true },
  { key: "invoice", label: "Invoice", rec: true },
  { key: "dispatch", label: "Dispatch", rec: true },
  { key: "transporter", label: "Transporter", rec: true },
  { key: "installer", label: "Installer", rec: true },
  { key: "annexure", label: "Annexure", doc: true },
  { key: "quotation", label: "Quotation", doc: true },
  { key: "bill", label: "Bill", doc: true },
  { key: "po", label: "PO", doc: true },
];

const READINESS_ITEMS = [
  { key: "handover_letter", label: "Handover Letter" },
  { key: "aadhaar", label: "Aadhaar" },
  { key: "pan", label: "PAN" },
  { key: "unloaded_photo", label: "Unloaded Photo" },
];

const DOSSIER_SECTIONS = [
  { key: "office_work", label: "Office Work" },
  { key: "invoice", label: "Invoice" },
  { key: "item", label: "Item Tracking" },
  { key: "dispatch", label: "Dispatch" },
  { key: "transporter", label: "Transporter" },
  { key: "installer", label: "Installer" },
  { key: "doc_index", label: "Document Index" },
];

export default function OADetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const can = (p) => user.role === "admin" || (user.permissions || []).includes(p);
  const [oa, setOa] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("details");
  const [dossierOpen, setDossierOpen] = useState(false);
  const [dossierOpts, setDossierOpts] = useState({ cover: false, office_work: true, invoice: true, item: true, dispatch: true, transporter: true, installer: true, doc_index: true });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/oas/${id}`); setOa(data);
        const { data: sm } = await api.get(`/oa/${id}/summary`); setSummary(sm);
      } catch (e) { toast.error(errMsg(e)); navigate("/oa"); }
    })();
  }, [id]); // eslint-disable-line

  if (!oa) return <div className="p-8 text-slate-500">Loading…</div>;
  const counts = summary?.counts || {};
  const dates = summary?.dates || {};
  const visibleTabs = TABS.filter((t) => (t.rec && can(`view_${t.key}`)) || (t.doc && can(t.key === "po" ? "view_po" : `view_${t.key}`)) || (!t.rec && !t.doc));

  const downloadJobCard = () => {
    api.get(`/oa/${id}/job-card`, { responseType: "blob" })
      .then(({ data }) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `job-card-${oa.oaNumber}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60000); })
      .catch((e) => toast.error(errMsg(e)));
  };
  const downloadDossier = () => {
    const sections = DOSSIER_SECTIONS.filter((s) => dossierOpts[s.key]).map((s) => s.key).join(",");
    if (!sections) { toast.error("Select at least one section."); return; }
    setDossierOpen(false);
    toast.info("Generating OA Dossier…");
    api.get(`/oa/${id}/dossier?cover=${dossierOpts.cover}&sections=${sections}`, { responseType: "blob" })
      .then(({ data }) => { const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = `oa-dossier-${oa.oaNumber}.pdf`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 60000); })
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div>
      <div className="sticky top-0 z-20 bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/oa")} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="h-4 w-4" /> OA History</button>
        <div className="font-head font-semibold flex items-center gap-3">OA <span className="font-mono">{oa.oaNumber}</span> <WorkStatusBadge status={oa.workStatus} /></div>
        <button className="btn-accent btn-sm" onClick={() => navigate(`/oa/${id}/preview`)} data-testid="oadetails-preview-btn"><Eye className="h-4 w-4" /> Preview / Print</button>
        <button className="btn-outline btn-sm border-slate-600 text-slate-200 hover:bg-white/10" onClick={downloadJobCard} data-testid="oadetails-jobcard-btn"><FileDown className="h-4 w-4" /> Job Card PDF</button>
        <button className="btn-outline btn-sm border-slate-600 text-slate-200 hover:bg-white/10" onClick={() => setDossierOpen(true)} data-testid="oadetails-dossier-btn"><FileDown className="h-4 w-4" /> OA Dossier</button>
        {can("add_workorder") && <button className="btn-outline btn-sm border-slate-600 text-slate-200 hover:bg-white/10" onClick={() => navigate(`/work-orders?create=${id}`)} data-testid="oadetails-create-wo-btn"><ClipboardList className="h-4 w-4" /> Create Work Order</button>}
      </div>

      {summary?.readiness && (
        <div className="bg-white border-b border-slate-200 px-6 py-2 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid="oa-readiness-strip">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Readiness</span>
          {READINESS_ITEMS.map((it) => {
            const ok = summary.readiness[it.key];
            return (
              <span key={it.key} className={`flex items-center gap-1.5 text-xs font-medium ${ok ? "text-green-700" : "text-slate-400"}`} data-testid={`readiness-${it.key}`}>
                {ok ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-slate-300" />}
                {it.label}
              </span>
            );
          })}
          {READINESS_ITEMS.every((it) => summary.readiness[it.key]) && (
            <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5" data-testid="readiness-complete-badge">Dispatch Ready</span>
          )}
        </div>
      )}

      <div className="bg-white border-b border-slate-200 px-6 flex gap-1 overflow-x-auto">
        {visibleTabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`oadetails-tab-${t.key}`}
            className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.key ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}{(t.rec || t.doc) && counts[t.key] ? <span className="ml-1 text-xs text-slate-400">({counts[t.key]})</span> : ""}
          </button>
        ))}
      </div>

      <div className="p-6 lg:p-8">
        {tab === "details" ? (
          <div className="space-y-4 max-w-4xl">
            <div className="card p-5" data-testid="oa-delivery-timeline">
              <h3 className="font-head font-semibold text-slate-900 mb-4">Delivery Timeline</h3>
              {(() => {
                const inst = oa.workStatus === "completed" || counts.installer > 0;
                const steps = [
                  { label: "Office Work", done: counts.office_work > 0, date: dates.office_work },
                  { label: "Dispatch", done: counts.dispatch > 0, date: dates.dispatch },
                  { label: "In Transit", done: counts.transporter > 0, date: dates.transporter },
                  { label: "Installed", done: inst && oa.workStatus === "completed", date: dates.installed },
                ];
                return (
                  <div className="flex items-center">
                    {steps.map((s, i) => (
                      <div key={s.label} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center">
                          <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${s.done ? "bg-green-600 text-white" : "bg-slate-200 text-slate-500"}`} data-testid={`timeline-step-${i}`}>{s.done ? "✓" : i + 1}</div>
                          <div className={`text-xs mt-1.5 text-center ${s.done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{s.label}</div>
                          {s.date && <div className="text-[10px] text-slate-400" data-testid={`timeline-date-${i}`}>{s.date}</div>}
                        </div>
                        {i < steps.length - 1 && <div className={`h-1 flex-1 mx-2 rounded ${steps[i + 1].done ? "bg-green-500" : "bg-slate-200"}`} />}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="card p-6 grid md:grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-400">Customer</span><div className="font-semibold">{oa.customerName}</div></div>
              <div><span className="text-slate-400">OA Date</span><div className="font-semibold">{oa.oaDate || "—"}</div></div>
              <div><span className="text-slate-400">PO Number</span><div className="font-semibold">{oa.partyPONumber || "—"}</div></div>
              <div><span className="text-slate-400">Net Total</span><div className="font-semibold">{inr(oa.netTotal)}</div></div>
              <div><span className="text-slate-400">Destination</span><div className="font-semibold">{oa.destination || "—"}</div></div>
              <div><span className="text-slate-400">Source</span><div className="font-semibold">{oa.source || "Created"}</div></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {["office_work", "invoice", "item", "dispatch", "transporter", "installer", "po", "annexure", "quotation", "bill"].map((k) => (
                <div key={k} className="card p-4 text-center" data-testid={`oadetails-count-${k}`}>
                  <div className="text-2xl font-head font-bold text-slate-900">{counts[k] || 0}</div>
                  <div className="text-xs text-slate-500 capitalize">{k.replace("_", " ")}</div>
                </div>
              ))}
            </div>
          </div>
        ) : TABS.find((t) => t.key === tab)?.doc ? (
          <OADocuments key={tab} docType={tab} oaId={id} oaNumber={oa.oaNumber} customerName={oa.customerName} poFile={oa.poFile} />
        ) : (
          <OARecordModule key={tab} recType={tab} fixedOaId={id} fixedOaNumber={oa.oaNumber} embedded />
        )}
      </div>

      {dossierOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="dossier-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h3 className="font-head font-semibold text-slate-900 mb-1">OA Dossier Options</h3>
            <p className="text-xs text-slate-500 mb-4">Choose the cover page and sections to include in the PDF.</p>
            <label className="flex items-center gap-2 py-1.5 text-sm text-slate-800">
              <input type="checkbox" checked={dossierOpts.cover} onChange={(e) => setDossierOpts({ ...dossierOpts, cover: e.target.checked })} data-testid="dossier-opt-cover" />
              <span className="font-medium">Cover Page <span className="text-slate-400 font-normal">(branding + OA summary)</span></span>
            </label>
            <div className="border-t border-slate-100 mt-2 pt-2">
              {DOSSIER_SECTIONS.map((s) => (
                <label key={s.key} className="flex items-center gap-2 py-1.5 text-sm text-slate-800">
                  <input type="checkbox" checked={dossierOpts[s.key]} onChange={(e) => setDossierOpts({ ...dossierOpts, [s.key]: e.target.checked })} data-testid={`dossier-opt-${s.key}`} />
                  {s.label}
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button className="btn-outline btn-sm" onClick={() => setDossierOpen(false)} data-testid="dossier-cancel-btn">Cancel</button>
              <button className="btn-accent btn-sm" onClick={downloadDossier} data-testid="dossier-generate-btn"><FileDown className="h-4 w-4" /> Generate PDF</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
